import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

type NetworkStatus = 'offline' | 'online';
interface ConnectivityContextProps {
  isOffline: boolean;
  connectivityStatus: NetworkStatus;
  checkConnectivity: () => Promise<boolean>;
  updateConnectivityStatus: (status: NetworkStatus) => void;
}

const NetworkStatusContext = createContext<ConnectivityContextProps>({} as ConnectivityContextProps);

const NetworkStatusProvider: React.FC<any> = ({ children }) => {
  const [connectivityStatus, setConnectivityStatus] = useState<NetworkStatus>('online');

  const updateConnectivityStatus = useCallback((status: NetworkStatus) => {
    setConnectivityStatus(status);
  }, []);

  const checkConnectivity = useCallback(() => {
    return fetch('/favicon.ico', { method: 'HEAD', cache: 'no-cache', signal: AbortSignal.timeout(3000) })
      .then(() => {
        setConnectivityStatus('online');
        return true
      })
      .catch(() => {
        setConnectivityStatus('offline');
        return false;
      });
  }, []);

  useEffect(() => {
    const handleOnline = () => setConnectivityStatus('online');
    const handleOffline = () => setConnectivityStatus('offline');

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    checkConnectivity();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const isOffline = connectivityStatus === 'offline';

  const contextValue = useMemo(
    () => ({
      isOffline,
      connectivityStatus,
      checkConnectivity,
      updateConnectivityStatus,
    }),
    [connectivityStatus, checkConnectivity, updateConnectivityStatus]
  );

  return (
    <NetworkStatusContext.Provider value={contextValue}>
      {children}
    </NetworkStatusContext.Provider>
  )
}

const useNetworkStatusContext = () => {
  const ctx = useContext(NetworkStatusContext);

  if (!ctx) throw new Error("Erro ao usar hook Connectivity Context");

  return ctx;
}

export { NetworkStatusProvider, useNetworkStatusContext };